/**
 * FoodPulseLocationBar
 * --------------------
 * Compact "You are in <City · State · Country> — Change" strip shown at
 * the top of every /food-pulse page. On first visit it fires:
 *   1. Browser Geolocation (opportunistic, no prompt suppression).
 *   2. Server-side IP geolocation via /api/ip-geolocate (also handles
 *      reverse-geocode of coords → city/state/country).
 * Once resolved, city / state / country are persisted in localStorage
 * ('fp_user_location') and broadcast via a window CustomEvent
 * 'fp:location:change' so other components can react.
 *
 * The user can override any level via three cascading dropdowns —
 * country pulls from /api/geo/countries, state from
 * /api/geo/subdivisions?country=XX, and city is a free-text input
 * (city catalogues are far too large to enumerate).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapPin, ChevronDown, Loader2, Check, X, Locate } from "lucide-react";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const PURPLE = "#7C3AED";
const STORAGE_KEY = "fp_user_location";
const LEGACY_KEY = "fp:userLocation";  // LocationPicker + LocalDealsHero use this

export function getStoredLocation() {
  try {
    const cur = localStorage.getItem(STORAGE_KEY);
    if (cur) return JSON.parse(cur);
    // Fall back to the legacy picker's location so existing users don't lose their choice.
    const legacy = localStorage.getItem(LEGACY_KEY);
    return legacy ? JSON.parse(legacy) : null;
  } catch { return null; }
}

export function useFoodPulseLocation() {
  const [loc, setLoc] = useState(getStoredLocation);
  useEffect(() => {
    const onChange = (e) => setLoc(e.detail || getStoredLocation());
    const onStorage = (e) => { if (e.key === STORAGE_KEY || e.key === LEGACY_KEY) setLoc(getStoredLocation()); };
    window.addEventListener("fp:location:change", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("fp:location:change", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return loc;
}

function persist(loc) {
  if (loc) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    // Mirror into the legacy key so LocalDealsHero + LocationPicker stay in sync.
    // Normalise to the shape the legacy code expects.
    localStorage.setItem(LEGACY_KEY, JSON.stringify({
      city: loc.city,
      formatted_address: [loc.city, loc.state, loc.country].filter(Boolean).join(", "),
      latitude: loc.lat, longitude: loc.lon,
      country: loc.country, state: loc.state,
    }));
  } else {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  }
  window.dispatchEvent(new CustomEvent("fp:location:change", { detail: loc }));
}

async function reverseGeocode(lat, lon) {
  // Backend proxy — avoids CORS + rate limits + returns normalised fields.
  try {
    const r = await http.post("/geo/reverse", { latitude: lat, longitude: lon });
    return { city: r.data.city, state: r.data.state, country: r.data.country };
  } catch { /* fall back to Nominatim (public + CORS-friendly) */ }
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=10`, { headers: { Accept: "application/json" } });
    const d = await r.json();
    return { city: d.address?.city || d.address?.town || d.address?.village || d.address?.county, state: d.address?.state, country: d.address?.country };
  } catch { return null; }
}

async function ipGeolocate() {
  try { const r = await http.get("/geo/lookup"); return r.data; }
  catch { return null; }
}

export default function FoodPulseLocationBar() {
  const [loc, setLoc] = useState(getStoredLocation);
  const [detecting, setDetecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [draft, setDraft] = useState({ country: "", state: "", city: "" });
  const firedRef = useRef(false);

  // Auto-detect on first visit only.
  const autodetect = useCallback(async () => {
    if (firedRef.current) return;
    firedRef.current = true;
    setDetecting(true);
    let resolved = null;
    // 1) Browser geolocation.
    if (navigator.geolocation) {
      resolved = await new Promise((res) => {
        navigator.geolocation.getCurrentPosition(
          async (p) => {
            const rg = await reverseGeocode(p.coords.latitude, p.coords.longitude);
            res(rg ? { ...rg, lat: p.coords.latitude, lon: p.coords.longitude, source: "gps" } : null);
          },
          () => res(null),
          { timeout: 8000, maximumAge: 300000 },
        );
      });
    }
    // 2) IP fallback.
    if (!resolved) {
      const ip = await ipGeolocate();
      if (ip) resolved = { city: ip.city, state: ip.region || ip.state, country: ip.country_name || ip.country, lat: ip.latitude, lon: ip.longitude, source: "ip" };
    }
    if (resolved && resolved.city) {
      persist(resolved);
      setLoc(resolved);
    }
    setDetecting(false);
  }, []);

  useEffect(() => {
    if (!loc) autodetect();
  }, [loc, autodetect]);

  const openEdit = async () => {
    setDraft({ country: loc?.country || "", state: loc?.state || "", city: loc?.city || "" });
    setEditing(true);
    if (!countries.length) {
      try { const r = await http.get("/geo/countries"); setCountries(r.data.countries || []); } catch {}
    }
  };

  const country = useMemo(() => countries.find((c) => c.name === draft.country || c.code === draft.country), [countries, draft.country]);
  useEffect(() => {
    if (!country) { setRegions([]); return; }
    http.get("/geo/subdivisions", { params: { country: country.code } })
      .then((r) => setRegions(r.data.subdivisions || r.data.items || []))
      .catch(() => setRegions([]));
  }, [country]);

  const commit = () => {
    if (!draft.country && !draft.state && !draft.city) return;
    const next = { country: draft.country, state: draft.state, city: draft.city, source: "manual" };
    persist(next); setLoc(next); setEditing(false);
  };

  const label = loc ? [loc.city, loc.state, loc.country].filter(Boolean).join(" · ") : (detecting ? "Detecting your location…" : "Location not set");

  return (
    <div className="w-full border-b" style={{ background: CREAM, borderColor: "#E9D8B4" }} data-testid="fp-location-bar">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center flex-wrap gap-2 text-sm">
        <MapPin size={14} style={{ color: PURPLE }} />
        <span className="text-gray-700">You're in</span>
        <b style={{ color: GREEN }} data-testid="fp-loc-label">{label}</b>
        {detecting && <Loader2 size={12} className="animate-spin text-gray-500" />}
        <button onClick={openEdit} className="ml-auto rounded-full px-3 py-1 text-xs font-semibold border" style={{ borderColor: PURPLE, color: PURPLE }} data-testid="fp-loc-change-btn">
          Change <ChevronDown size={10} className="inline" />
        </button>
        <button onClick={() => { firedRef.current = false; autodetect(); }} title="Auto-detect again" className="rounded-full px-2 py-1 text-xs border" style={{ borderColor: GREEN, color: GREEN }} data-testid="fp-loc-redetect-btn">
          <Locate size={10} className="inline" />
        </button>
      </div>
      {editing && (
        <div className="max-w-6xl mx-auto px-4 pb-3 grid md:grid-cols-4 gap-2" data-testid="fp-loc-editor">
          <select value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value, state: "" })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="fp-loc-country">
            <option value="">Country…</option>
            {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
          </select>
          <select value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} disabled={!regions.length} className="border rounded-lg px-3 py-2 text-sm disabled:opacity-50" style={{ borderColor: "#E9D8B4" }} data-testid="fp-loc-state">
            <option value="">State / Region…</option>
            {regions.map((r) => <option key={r.code || r.name} value={r.name}>{r.name}</option>)}
          </select>
          <input value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder="City" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="fp-loc-city" />
          <div className="flex gap-2">
            <button onClick={commit} className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="fp-loc-apply"><Check size={12} className="inline mr-1" />Apply</button>
            <button onClick={() => setEditing(false)} className="rounded-lg px-3 py-2 text-sm font-semibold border" style={{ borderColor: "#E9D8B4", color: GREEN }} data-testid="fp-loc-cancel"><X size={12} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
